import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip, Stack, useMediaQuery, useTheme } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const FIELD_LABELS = {
	agent_id: "Agent ID",
	first_name: "First Name",
	last_name: "Last Name",
	suffix: "Suffix",
	address: "Address",
	unit: "Unit",
	city: "City",
	state: "State",
	zip_code: "Zip Code",
	county: "County",
	mailing_address: "Mailing Address",
	mailing_city: "Mailing City",
	mailing_state: "Mailing State",
	mailing_zip_code: "Mailing Zip",
	phone: "Phone",
	email: "Email",
	applicant_count: "Applicant Count",
	policy_status: "Policy Status",
	carrier_provided_identifier: "Carrier ID / Polciy ID",
	issuer: "Issuer / Carrier",
	effective_date: "Effective Date",
};

// Fields returned by the backend row that we don't want rendered in the compare grid
const EXCLUDED_FIELDS = ["id", "checksum", "created_at", "updated_at", "healthsherpa_id"];

const FIELDS = Object.keys(FIELD_LABELS);

// ─── Action choice card ────────────────────────────────────────────────────────
const ActionCard = ({ value, selected, onSelect, icon, label, description, accentColor, accentBg, isMobile }) => (
	<Box
		onClick={() => onSelect(value)}
		sx={{
			flex: 1,
			p: isMobile ? "10px 12px" : 1.5,
			borderRadius: "10px",
			border: selected ? `2px solid ${accentColor}` : "2px solid #e8ecf2",
			backgroundColor: selected ? accentBg : "#fff",
			cursor: "pointer",
			transition: "all 0.15s ease",
			display: "flex",
			flexDirection: isMobile ? "row" : "column",
			alignItems: "center",
			gap: isMobile ? 1.25 : 0.75,
			textAlign: isMobile ? "left" : "center",
			position: "relative",
			userSelect: "none",
			"&:hover": { borderColor: accentColor, backgroundColor: accentBg },
		}}
	>
		{selected && <CheckCircleIcon sx={{ position: "absolute", top: 6, right: 6, fontSize: 15, color: accentColor }} />}
		<Box
			sx={{
				width: isMobile ? 32 : 36,
				height: isMobile ? 32 : 36,
				minWidth: isMobile ? 32 : 36,
				borderRadius: "8px",
				backgroundColor: selected ? accentColor : "#f0f2f7",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: selected ? "#fff" : "#888",
				transition: "all 0.15s ease",
			}}
		>
			{icon}
		</Box>
		<Box sx={{ flex: isMobile ? 1 : "unset", minWidth: 0 }}>
			<Typography sx={{ fontSize: "12px", fontWeight: 700, color: selected ? accentColor : "#333", lineHeight: 1.2 }}>{label}</Typography>
			<Typography sx={{ fontSize: "10.5px", color: "#888", lineHeight: 1.4, mt: isMobile ? 0.25 : 0 }}>{description}</Typography>
		</Box>
	</Box>
);

// ─── Single conflict panel ─────────────────────────────────────────────────────
const ConflictPanel = ({ conflict, action, onActionChange, isLast }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const visibleFields = FIELDS.filter((f) => !EXCLUDED_FIELDS.includes(f));
	// backend-provided diff is authoritative; fall back to empty if absent
	const diffFields = Object.keys(conflict.diff || {});
	const name = `${conflict.existing?.first_name ?? ""} ${conflict.existing?.last_name ?? ""}`.trim();

	return (
		<Box sx={{ mb: isLast ? 0 : 3, pb: isLast ? 0 : 3, borderBottom: isLast ? "none" : "1px solid #edf0f7" }}>
			{/* Record header */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
				<Box
					sx={{
						width: 36,
						height: 36,
						minWidth: 36,
						borderRadius: "50%",
						backgroundColor: "#fff3e0",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 24 }} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#1a1a1a" }}>{name || "Unnamed record"}</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
						<Typography sx={{ fontSize: "11px", color: "#888" }}>Existing ID:</Typography>
						<Typography sx={{ fontSize: "11px", fontFamily: "monospace", fontWeight: 600, color: "#1D54A2" }}>
							{conflict.existing_id}
						</Typography>
						<Chip
							label={`${diffFields.length} field${diffFields.length !== 1 ? "s" : ""} differ`}
							size="small"
							sx={{ fontSize: "10px", height: 17, backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 600, border: "none" }}
						/>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
						<Typography sx={{ fontSize: "11px", color: "#888" }}>Agent Name:</Typography>
						<Typography sx={{ fontSize: "11px", fontFamily: "monospace", fontWeight: 600, color: "#1D54A2" }}>
							{conflict.agent_name}
						</Typography>
					</Box>
				</Box>
			</Box>

			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 2.5 }}>
				{/* Existing column */}
				<Box sx={{ borderRadius: "10px", border: "1px solid #e8ecf2", overflow: "hidden" }}>
					<Box sx={{ px: 1.5, py: 0.875, backgroundColor: "#f5f7fb", borderBottom: "1px solid #e8ecf2" }}>
						<Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em" }}>
							Existing Record
						</Typography>
					</Box>
					<Box sx={{ p: 1 }}>
						{visibleFields.map((field) => {
							const isDiff = diffFields.includes(field);
							const val = String(conflict.existing?.[field] ?? "");
							return (
								<Box
									key={field}
									sx={{
										px: 1,
										py: 0.6,
										borderRadius: "6px",
										backgroundColor: isDiff ? "rgba(211, 47, 47, 0.05)" : "transparent",
										display: "flex",
										flexDirection: "column",
										gap: 0.1,
									}}
								>
									<Typography
										sx={{ fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500 }}
									>
										{FIELD_LABELS[field]}
									</Typography>
									<Typography
										sx={{
											fontSize: "12.5px",
											color: isDiff ? "#b71c1c" : "#444",
											fontWeight: isDiff ? 600 : 400,
											wordBreak: "break-all",
											textDecoration: isDiff ? "line-through" : "none",
											opacity: isDiff ? 0.8 : 1,
										}}
									>
										{val || <span style={{ color: "#ccc", fontStyle: "italic" }}>—</span>}
									</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>

				{/* Incoming column */}
				<Box sx={{ borderRadius: "10px", border: "1px solid #e8ecf2", overflow: "hidden" }}>
					<Box sx={{ px: 1.5, py: 0.875, backgroundColor: "#f0f7f0", borderBottom: "1px solid #c8e6c9" }}>
						<Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2e7d32", textTransform: "uppercase", letterSpacing: "0.06em" }}>
							Incoming (New)
						</Typography>
					</Box>
					<Box sx={{ p: 1 }}>
						{visibleFields.map((field) => {
							const isDiff = diffFields.includes(field);
							const val = String(conflict.incoming?.[field] ?? "");
							return (
								<Box
									key={field}
									sx={{
										px: 1,
										py: 0.6,
										borderRadius: "6px",
										backgroundColor: isDiff ? "rgba(46, 125, 50, 0.06)" : "transparent",
										display: "flex",
										flexDirection: "column",
										gap: 0.1,
									}}
								>
									<Typography
										sx={{ fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500 }}
									>
										{FIELD_LABELS[field]}
									</Typography>
									<Typography
										sx={{
											fontSize: "12.5px",
											color: isDiff ? "#1b5e20" : "#444",
											fontWeight: isDiff ? 700 : 400,
											wordBreak: "break-all",
										}}
									>
										{val || <span style={{ color: "#ccc", fontStyle: "italic" }}>—</span>}
									</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Box>

			{/* Action cards */}
			<Box>
				<Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
					What would you like to do?
				</Typography>
				<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25 }}>
					<ActionCard
						value="overwrite"
						selected={action === "overwrite"}
						onSelect={onActionChange}
						icon={<SwapHorizIcon sx={{ fontSize: 17 }} />}
						label="Overwrite"
						description="Replace existing with incoming data"
						accentColor="#1D54A2"
						accentBg="#eef3fb"
						isMobile={isMobile}
					/>
					<ActionCard
						value="add_new"
						selected={action === "add_new"}
						onSelect={onActionChange}
						icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
						label="Add as New"
						description="Keep existing, save incoming as a new record"
						accentColor="#2e7d32"
						accentBg="#f0f7f0"
						isMobile={isMobile}
					/>
					<ActionCard
						value="ignore"
						selected={action === "ignore"}
						onSelect={onActionChange}
						icon={<BlockIcon sx={{ fontSize: 17 }} />}
						label="Ignore"
						description="Discard incoming, keep existing unchanged"
						accentColor="#757575"
						accentBg="#f5f5f5"
						isMobile={isMobile}
					/>
				</Box>
			</Box>
		</Box>
	);
};

// ─── Confirm summary dialog ────────────────────────────────────────────────────
const ConfirmSummaryDialog = ({ open, onBack, onConfirm, conflicts, actions, loading }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const rows = conflicts.map((c) => ({
		...c,
		action: actions[c.existing_id],
	}));

	const ACTION_META = {
		overwrite: { label: "Overwrite", bg: "#eef3fb", color: "#1D54A2", icon: <SwapHorizIcon sx={{ fontSize: 14 }} /> },
		add_new: { label: "Add as New", bg: "#f0f7f0", color: "#2e7d32", icon: <AddCircleOutlineIcon sx={{ fontSize: 14 }} /> },
		ignore: { label: "Ignore", bg: "#f5f5f5", color: "#757575", icon: <BlockIcon sx={{ fontSize: 14 }} /> },
	};

	return (
		<Dialog
			open={open}
			maxWidth="sm"
			fullWidth
			fullScreen={isMobile}
			disableEscapeKeyDown
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : "14px",
					backgroundColor: "#f9fbfd",
					border: isMobile ? "none" : "1px solid #e0e0e0",
					overflow: "hidden",
				},
			}}
		>
			<DialogTitle
				sx={{
					borderBottom: "1px solid #e8ecf2",
					backgroundColor: "#fff",
					py: 1.75,
					px: { xs: 2, sm: 3 },
					display: "flex",
					alignItems: "center",
					gap: 1.5,
				}}
			>
				<WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 22 }} />
				<Box>
					<Typography sx={{ fontWeight: 700, fontSize: { xs: "0.9rem", sm: "1rem" }, color: "#1a1a1a", lineHeight: 1.2 }}>
						Confirm Changes
					</Typography>
					<Typography sx={{ fontSize: "12px", color: "#888", mt: 0.25 }}>Review before applying. This cannot be undone.</Typography>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
				<Stack spacing={1.25}>
					{rows.map((row) => {
						const meta = ACTION_META[row.action];
						const name = `${row.existing?.first_name ?? ""} ${row.existing?.last_name ?? ""}`.trim();
						return (
							<Box
								key={row.existing_id}
								sx={{
									display: "flex",
									flexDirection: { xs: "column", sm: "row" },
									alignItems: "flex-start",
									gap: 1.25,
									p: { xs: 1.5, sm: 1.75 },
									borderRadius: "10px",
									border: "1px solid #e8ecf2",
									backgroundColor: "#fff",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										px: 1.25,
										py: 0.5,
										borderRadius: "6px",
										backgroundColor: meta?.bg,
										color: meta?.color,
										fontWeight: 600,
										fontSize: "11px",
										whiteSpace: "nowrap",
										minWidth: { xs: "auto", sm: 100 },
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									{meta?.icon}&nbsp;{meta?.label}
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#222", lineHeight: 1.3 }}>
										{name || "Unnamed record"}
									</Typography>
									{row.action === "overwrite" && (
										<Typography sx={{ fontSize: "12px", color: "#666", mt: 0.25 }}>
											Record{" "}
											<span style={{ fontFamily: "monospace", color: "#1D54A2", fontWeight: 600 }}>{row.existing_id}</span> will
											be overwritten with the incoming data.
										</Typography>
									)}
									{row.action === "add_new" && (
										<Typography sx={{ fontSize: "12px", color: "#666", mt: 0.25 }}>
											Existing{" "}
											<span style={{ fontFamily: "monospace", color: "#1D54A2", fontWeight: 600 }}>{row.existing_id}</span>{" "}
											kept. Incoming will be inserted as a new record.
										</Typography>
									)}
									{row.action === "ignore" && (
										<Typography sx={{ fontSize: "12px", color: "#666", mt: 0.25 }}>
											Incoming record matching{" "}
											<span style={{ fontFamily: "monospace", color: "#555", fontWeight: 600 }}>{row.existing_id}</span> will be
											discarded.
										</Typography>
									)}
								</Box>
							</Box>
						);
					})}
				</Stack>
			</DialogContent>

			<DialogActions
				sx={{ borderTop: "1px solid #e8ecf2", backgroundColor: "#fff", px: { xs: 2, sm: 3 }, py: 1.75, justifyContent: "space-between" }}
			>
				<Button
					onClick={onBack}
					size="small"
					sx={{
						textTransform: "none",
						fontSize: "13px",
						color: "#555",
						border: "1px solid #e0e0e0",
						borderRadius: "8px",
						px: 2,
						backgroundColor: "#fff !important",
						"&:hover": { borderColor: "#aaa", backgroundColor: "#f5f5f5 !important" },
					}}
				>
					← Back
				</Button>
				<Button
					onClick={onConfirm}
					variant="contained"
					size="small"
					loading={loading}
					sx={{
						textTransform: "none",
						fontSize: "13px",
						backgroundColor: "#1D54A2 !important",
						borderRadius: "8px",
						px: 2.5,
						"&:hover": { backgroundColor: "#163f7c !important" },
						"&:disabled": { backgroundColor: "transparent !important", border: "1px solid #1d54a234" },
					}}
				>
					Confirm & Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ─── Main dialog ───────────────────────────────────────────────────────────────
const ExistingRowConflictDialog = ({ open, duplicates, onClose, onConfirm, loading }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const conflicts = duplicates || [];

	const [actions, setActions] = useState({});
	const [showConfirm, setShowConfirm] = useState(false);

	useEffect(() => {
		if (!loading) {
			setShowConfirm(false);
		}
	}, [loading]);

	// reset/seed default actions whenever a new batch of conflicts comes in
	useEffect(() => {
		if (conflicts.length) {
			setActions(Object.fromEntries(conflicts.map((c) => [c.existing_id, "overwrite"])));
			setShowConfirm(false);
		}
	}, [duplicates]);

	const handleActionChange = (existingId, value) => {
		setActions((prev) => ({ ...prev, [existingId]: value }));
	};

	const handleFinalConfirm = () => {
		const result = conflicts.map((c) => ({
			existing_id: c.existing_id,
			action: actions[c.existing_id],
			incoming: c.incoming,
			diff: c.diff,
		}));
		onConfirm?.(result);
	};

	if (!conflicts.length) return null;

	const allResolved = conflicts.every((c) => actions[c.existing_id]);

	const summary = {
		overwrite: Object.values(actions).filter((a) => a === "overwrite").length,
		add_new: Object.values(actions).filter((a) => a === "add_new").length,
		ignore: Object.values(actions).filter((a) => a === "ignore").length,
	};

	return (
		<>
			<Dialog
				open={open && !showConfirm}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				fullScreen={isMobile}
				disableEscapeKeyDown
				PaperProps={{
					sx: {
						borderRadius: isMobile ? 0 : "16px",
						backgroundColor: "#f9fbfd",
						border: isMobile ? "none" : "1px solid #e0e0e0",
						overflow: "hidden",
						width: { xs: "100%", md: "900px" },
						maxWidth: { xs: "100%", md: "900px !important" },
					},
				}}
			>
				<DialogTitle
					sx={{
						borderBottom: "1px solid #e8ecf2",
						backgroundColor: "#fff",
						py: { xs: 1.5, sm: 2 },
						px: { xs: 2, sm: 3 },
						display: "flex",
						alignItems: "flex-start",
						gap: 1.25,
					}}
				>
					<Box
						sx={{
							width: { xs: 36, sm: 42 },
							height: { xs: 36, sm: 42 },
							minWidth: { xs: 36, sm: 42 },
							borderRadius: "10px",
							backgroundColor: "#fff8e1",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<WarningAmberIcon sx={{ color: "#f59e0b", fontSize: { xs: 20, sm: 24 } }} />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ fontWeight: 700, fontSize: { xs: "0.9rem", sm: "1.05rem" }, color: "#1a1a1a", lineHeight: 1.2 }}>
							Conflicting Records Detected
						</Typography>
						<Typography sx={{ fontSize: { xs: "11px", sm: "12.5px" }, color: "#888", mt: 0.3, lineHeight: 1.4 }}>
							{conflicts.length} record{conflicts.length !== 1 ? "s" : ""} in the uploaded file differ from existing data.
						</Typography>
						<Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", gap: "5px !important" }}>
							{summary.overwrite > 0 && (
								<Chip
									icon={<SwapHorizIcon sx={{ fontSize: "12px !important" }} />}
									label={`${summary.overwrite} overwrite`}
									size="small"
									sx={{ fontSize: "10.5px", height: 20, backgroundColor: "#eef3fb", color: "#1D54A2", fontWeight: 600 }}
								/>
							)}
							{summary.add_new > 0 && (
								<Chip
									icon={<AddCircleOutlineIcon sx={{ fontSize: "12px !important" }} />}
									label={`${summary.add_new} add as new`}
									size="small"
									sx={{ fontSize: "10.5px", height: 20, backgroundColor: "#f0f7f0", color: "#2e7d32", fontWeight: 600 }}
								/>
							)}
							{summary.ignore > 0 && (
								<Chip
									icon={<BlockIcon sx={{ fontSize: "12px !important" }} />}
									label={`${summary.ignore} ignore`}
									size="small"
									sx={{ fontSize: "10.5px", height: 20, backgroundColor: "#f5f5f5", color: "#757575", fontWeight: 600 }}
								/>
							)}
						</Stack>
					</Box>
				</DialogTitle>

				<DialogContent
					sx={{
						px: { xs: 2, sm: 3 },
						py: { xs: 2, sm: 3 },
						maxHeight: isMobile ? "none" : "68vh",
						overflowY: "auto",
						mt: { xs: 1.5, sm: 2 },
					}}
				>
					{conflicts.map((conflict, idx) => (
						<ConflictPanel
							key={conflict.existing_id}
							conflict={conflict}
							action={actions[conflict.existing_id]}
							onActionChange={(val) => handleActionChange(conflict.existing_id, val)}
							isLast={idx === conflicts.length - 1}
						/>
					))}
				</DialogContent>

				<DialogActions
					sx={{
						borderTop: "1px solid #e8ecf2",
						backgroundColor: "#fff",
						px: { xs: 2, sm: 3 },
						py: { xs: 1.75, sm: 2 },
						justifyContent: "space-between",
						gap: 1,
					}}
				>
					<Button
						onClick={onClose}
						size="small"
						sx={{
							textTransform: "none",
							fontSize: "13px",
							color: "#e74c3c",
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
							px: 2.5,
							backgroundColor: "#fff !important",
							"&:hover": { borderColor: "#e74c3c", backgroundColor: "#fbe9e7 !important" },
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={() => setShowConfirm(true)}
						disabled={!allResolved}
						variant="contained"
						size="small"
						sx={{
							textTransform: "none",
							fontSize: "13px",
							backgroundColor: "#1D54A2 !important",
							borderRadius: "8px",
							px: { xs: 2, sm: 3 },
							py: 0.875,
							"&:hover": { backgroundColor: "#163f7c !important" },
							"&.Mui-disabled": { backgroundColor: "#1D54A2 !important", color: "#fff", opacity: 0.5 },
						}}
					>
						Review & Apply →
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmSummaryDialog
				open={showConfirm}
				onBack={() => setShowConfirm(false)}
				onConfirm={handleFinalConfirm}
				conflicts={conflicts}
				actions={actions}
				loading={loading}
			/>
		</>
	);
};

export default ExistingRowConflictDialog;
